import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import cominterLogo from '../../assets/images/cominter-logo.png';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface SubMenuItem {
  id: string;
  label: string;
  path: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  badge?: string;
  submenu?: SubMenuItem[];
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/dashboard' },
  { 
    id: 'operations', 
    label: 'Operaciones', 
    icon: 'ri-ship-line', 
    badge: '8',
    submenu: [
      { id: 'operations-inbox', label: 'Bandeja de Operaciones', path: '/operations' },
      { id: 'operations-new', label: 'Nueva Operación', path: '/operations/new' },
      { id: 'operations-risk', label: 'Operaciones en Riesgo', path: '/operations/risk' },
      { id: 'operations-tasks', label: 'Tareas Operativas', path: '/operations/tasks' },
      { id: 'operations-incidents', label: 'Incidencias', path: '/operations/incidents' },
      { id: 'operations-documents', label: 'Documentos', path: '/operations/documents' },
      { id: 'operations-workflow', label: 'Estados y Flujo', path: '/operations/workflow' },
      { id: 'operations-reports', label: 'Reportes Operativos', path: '/operations/reports' },
    ]
  },
  { 
    id: 'crm', 
    label: 'CRM', 
    icon: 'ri-user-star-line',
    submenu: [
      { id: 'crm-clients', label: 'Clientes', path: '/crm' },
      { id: 'crm-contacts', label: 'Contactos', path: '/crm/contacts' },
      { id: 'crm-opportunities', label: 'Oportunidades', path: '/crm/opportunities' },
      { id: 'crm-followups', label: 'Seguimientos', path: '/crm/followups' },
      { id: 'crm-statements', label: 'Estados de Cuenta', path: '/crm/statements' },
      { id: 'crm-contracts', label: 'Contratos y Condiciones', path: '/crm/contracts' },
      { id: 'crm-incidents', label: 'Incidencias de Cliente', path: '/crm/client-incidents' },
      { id: 'crm-reports', label: 'Reportes CRM', path: '/crm/reports' },
    ]
  },
  { 
    id: 'portal', 
    label: 'Portal Clientes', 
    icon: 'ri-global-line',
    submenu: [
      { id: 'portal-home', label: 'Inicio', path: '/portal' },
      { id: 'portal-operations', label: 'Mis Operaciones', path: '/portal/operations' },
      { id: 'portal-documents', label: 'Documentos', path: '/portal/documents' },
      { id: 'portal-statements', label: 'Estados de Cuenta', path: '/portal/statements' },
      { id: 'portal-invoices', label: 'Facturas', path: '/portal/invoices' },
      { id: 'portal-payments', label: 'Pagos', path: '/portal/payments' },
      { id: 'portal-messages', label: 'Mensajes', path: '/portal/messages' },
      { id: 'portal-profile', label: 'Perfil', path: '/portal/profile' },
      { id: 'portal-support', label: 'Soporte', path: '/portal/support' },
    ]
  },
  { 
    id: 'erp', 
    label: 'ERP Financiero', 
    icon: 'ri-money-dollar-circle-line',
    submenu: [
      { id: 'erp-dashboard', label: 'Dashboard Financiero', path: '/erp' },
      { id: 'erp-receivables', label: 'Cuentas por Cobrar', path: '/erp/receivables' },
      { id: 'erp-payables', label: 'Cuentas por Pagar', path: '/erp/payables' },
      { id: 'erp-banks', label: 'Bancos', path: '/erp/banks' },
      { id: 'erp-accounting', label: 'Contabilidad General', path: '/erp/accounting' },
      { id: 'erp-statements', label: 'Estados Financieros', path: '/erp/statements' },
      { id: 'erp-thirdparty', label: 'Cuenta Ajena', path: '/erp/thirdparty' },
      { id: 'erp-invoicing', label: 'Facturación', path: '/erp/invoicing' },
      { id: 'erp-catalog', label: 'Catálogo Contable', path: '/erp/catalog' },
      { id: 'erp-reports', label: 'Reportes Financieros', path: '/erp/reports' },
    ]
  },
  { id: 'reports', label: 'Reportes', icon: 'ri-bar-chart-box-line', path: '/reports' },
  { id: 'users', label: 'Usuarios', icon: 'ri-team-line', path: '/users' },
  { id: 'settings', label: 'Configuración', icon: 'ri-settings-3-line', path: '/settings' },
  { id: 'audit', label: 'Auditoría', icon: 'ri-file-list-3-line', path: '/audit' },
  { id: 'integrations', label: 'Integraciones', icon: 'ri-plug-line', path: '/integrations' },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Detectar qué menú debe estar expandido basado en la ruta actual
  useEffect(() => {
    const currentPath = location.pathname;
    const activeMenu = menuItems.find(item => 
      item.submenu?.some(sub => currentPath === sub.path || currentPath.startsWith(sub.path + '/'))
    );
    
    if (activeMenu && !expandedMenus.includes(activeMenu.id)) {
      setExpandedMenus([activeMenu.id]);
    }
  }, [location.pathname]);

  const toggleSubmenu = (menuId: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [menuId] // Solo permite un menú expandido a la vez
    );
  };

  const isSubmenuActive = (submenu?: SubMenuItem[]) => {
    if (!submenu) return false;
    return submenu.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  };

  return (
    <aside
      className={`bg-white border-r border-gray-200 transition-all duration-300 flex flex-col ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
        {!collapsed && (
          <img
            src={cominterLogo}
            alt="COMINTER"
            className="h-8 object-contain"
          />
        )}
        <button
          onClick={onToggle}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className={`ri-menu-${collapsed ? 'unfold' : 'fold'}-line text-lg text-[#6B7280]`}></i>
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {menuItems.map((item) => {
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus.includes(item.id);
            const isActive = item.path ? location.pathname === item.path : isSubmenuActive(item.submenu);
            
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    if (hasSubmenu && !collapsed) {
                      toggleSubmenu(item.id);
                    } else if (item.path) {
                      navigate(item.path);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white'
                      : 'text-[#6B7280] hover:bg-[#EAF2F8] hover:text-[#0A2A43]'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-[#EF4444] text-white rounded-full">
                          {item.badge}
                        </span>
                      )}
                      {hasSubmenu && (
                        <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-sm transition-transform`}></i>
                      )}
                    </>
                  )}
                </button>

                {/* Submenu */}
                {hasSubmenu && !collapsed && isExpanded && (
                  <ul className="mt-1 ml-8 space-y-1">
                    {item.submenu!.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <li key={subItem.id}>
                          <button
                            onClick={() => navigate(subItem.path)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                              isSubActive
                                ? 'bg-[#EAF2F8] text-[#0A2A43] font-medium'
                                : 'text-[#6B7280] hover:bg-[#EAF2F8] hover:text-[#0A2A43]'
                            }`}
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                            <span className="flex-1 text-left">{subItem.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-gray-200">
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#114B7A] to-[#2F80ED] flex items-center justify-center text-white font-semibold">
            JD
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A2A43] truncate">Juan Delgado</p>
              <p className="text-xs text-[#6B7280] truncate">Administrador</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
